import { useState } from "react";
import { Plus, Minus } from "lucide-react";

// ============================================================
// FAQ — edit questions/answers below. Grouped by service so
// visitors quickly find what applies to them.
// ============================================================

type FAQ = { q: string; a: string };
type Group = { label: string; items: FAQ[] };

const GROUPS: Group[] = [
  {
    label: "Domestic cleaning",
    items: [
      {
        q: "How often can you clean my home?",
        a: "Weekly, fortnightly, or monthly — whatever fits your routine. We also do one-off cleans. Regular customers usually keep the same cleaner each visit for consistency.",
      },
      {
        q: "Do I need to provide cleaning products and equipment?",
        a: "No. We bring everything — professional-grade products, vacuum, mop and cloths. If you have specific products you'd prefer we use (e.g. for allergies or pets), just let us know in advance.",
      },
      {
        q: "How much does a regular clean cost?",
        a: "Domestic cleans start from £22/hr. We'll agree a fixed weekly or fortnightly price after a quick chat about your property — no surprises, no hidden fees.",
      },
      {
        q: "Do I need to be home during the clean?",
        a: "Not at all. Many customers give us a key or entry code once we've built trust. All our cleaners are fully insured and background-checked.",
      },
    ],
  },
  {
    label: "End of tenancy",
    items: [
      {
        q: "Will your clean pass the letting-agent inspection?",
        a: "We follow a detailed inventory-clerk checklist covering ovens, cupboard interiors, skirtings, extractor fans, limescale, windows and more. If your agent flags anything, we'll come back and put it right.",
      },
      {
        q: "How much does end-of-tenancy cleaning cost?",
        a: "End-of-tenancy cleans start from £160 depending on property size and condition. We'll give you a fixed quote within 2 hours of you sending your details — no per-hour surprises.",
      },
      {
        q: "How long does it take?",
        a: "A typical 2-bed flat takes 4–6 hours. Larger houses or heavily-lived-in properties take longer. We work efficiently as a team so you get the keys back to the agent on time.",
      },
      {
        q: "Do you clean carpets and ovens?",
        a: "Ovens and carpet cleaning is an add-on — we recommend it if your tenancy agreement requires professional oven and carpet cleaning. Ask for a combined quote and we'll bundle it in.",
      },
    ],
  },
  {
    label: "Military march-out",
    items: [
      {
        q: "Do you clean for MOD march-out inspections?",
        a: "Yes — march-out cleaning is one of our specialities. We know exactly what CarillionAmey / Pinnacle / VIVO inspectors check for and clean to that exact standard so you pass first time.",
      },
      {
        q: "What if I fail the inspection?",
        a: "If anything is flagged after our clean, we come back and put it right at no extra cost. In practice this rarely happens because we clean to inspection standard the first time.",
      },
      {
        q: "How much notice do you need for a march-out clean?",
        a: "Ideally 3–5 days, but we regularly help families at short notice. Call or WhatsApp us and we'll do everything we can to fit you in around your handover date.",
      },
      {
        q: "Which patches do you cover?",
        a: "Tidworth, Bulford, Larkhill, Ludgershall, Netheravon, Perham Down and the wider Salisbury Plain SFA estate — plus Andover and Salisbury. If you're nearby, just ask.",
      },
    ],
  },
  {
    label: "Local coverage & booking",
    items: [
      {
        q: "Do you provide cleaning services in Tidworth?",
        a: "Yes — Tidworth is our home patch. We regularly clean SFA and civilian addresses across Tidworth (SP9), including domestic, deep, end-of-tenancy and march-out cleans.",
      },
      {
        q: "Do you offer end of tenancy cleaning in Andover?",
        a: "Yes. We provide inspection-ready end of tenancy cleaning across Andover (SP10, SP11), working to letting-agent checklists so tenants get their deposit back.",
      },
      {
        q: "Do you provide military march-out cleaning near Salisbury Plain?",
        a: "Yes — march-out is our speciality. We cover Tidworth, Bulford, Larkhill, Ludgershall, Netheravon and Perham Down SFA, cleaning to the exact standard CarillionAmey / Pinnacle / VIVO inspectors expect.",
      },
      {
        q: "Do you offer Airbnb cleaning between guests?",
        a: "Yes. We run consistent turnaround cleans for Airbnb hosts and short-let owners across Amesbury, Salisbury and the wider Salisbury Plain area — including linen changes and essentials restock on request.",
      },
      {
        q: "How do I request a cleaning quote?",
        a: "Fill in the short form on our Get a Quote page, call 07763 933 920, WhatsApp us, or email info@izhubcleaning.uk. We usually reply within 2 hours during business hours with a clear, no-obligation price.",
      },
      {
        q: "How soon can I book a clean?",
        a: "Often within 24–72 hours for regular and one-off cleans. End-of-tenancy and march-out slots book up faster near month-end and around posting cycles — the earlier you contact us the more flexible we can be.",
      },
    ],
  },
];

export function FAQ() {
  const [openKey, setOpenKey] = useState<string>("0-0");

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: GROUPS.flatMap((g) =>
      g.items.map((it) => ({
        "@type": "Question",
        name: it.q,
        acceptedAnswer: { "@type": "Answer", text: it.a },
      }))
    ),
  };

  return (
    <section className="py-20 md:py-28" id="faq">
      <div className="container-x">
        <div className="text-surface max-w-2xl">
          <span className="eyebrow">Common questions</span>
          <h2 className="mt-4 text-3xl md:text-5xl">
            Everything you need to <span className="text-primary">know before booking</span>.
          </h2>
          <p className="mt-4 text-muted-foreground">
            Straight answers about our domestic, end-of-tenancy and military march-out cleans.
            Something not covered? <a href="/contact" className="text-primary underline underline-offset-4 font-medium">Ask us directly</a>.
          </p>
        </div>

        <div className="mt-12 grid gap-10 lg:grid-cols-3">
          {GROUPS.map((group, gi) => (
            <div key={group.label}>
              <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-primary mb-4">
                {group.label}
              </h3>
              <ul className="space-y-3">
                {group.items.map((it, ii) => {
                  const key = `${gi}-${ii}`;
                  const isOpen = openKey === key;
                  return (
                    <li
                      key={key}
                      className="rounded-2xl border border-border bg-card overflow-hidden"
                    >
                      <button
                        type="button"
                        onClick={() => setOpenKey(isOpen ? "" : key)}
                        aria-expanded={isOpen}
                        className="w-full flex items-start justify-between gap-4 p-5 text-left hover:bg-cream/50 transition-colors"
                      >
                        <span className="font-semibold text-sm text-ink leading-snug">
                          {it.q}
                        </span>
                        <span
                          className={`shrink-0 grid place-items-center h-7 w-7 rounded-full border border-border transition-colors ${
                            isOpen ? "bg-primary text-primary-foreground border-primary" : "text-primary"
                          }`}
                          aria-hidden="true"
                        >
                          {isOpen ? <Minus className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                        </span>
                      </button>
                      {isOpen && (
                        <div className="px-5 pb-5 -mt-1 text-sm text-muted-foreground leading-relaxed">
                          {it.a}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />
    </section>
  );
}
